import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

//MUI
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";

import FormikControl from "../../components/formik/FormikControl";
import Loader from "../../components/loader/Loader";
import ConfirmDialog from "../ConfirmDialog";

import ProjectService from "../../services/ProjectService";
import { Mode } from "./CountryPage";
import { CountryModel } from "../../models/CountryModel";
import { getAllCountries } from "../../redux/actions/CountryActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";

const validationSchema = Yup.object({
  name: Yup.string().required("Required"),
  code: Yup.string().required("Required"),
});

type FormProps = Yup.InferType<typeof validationSchema>;

type Props = {
  mode: Mode;
  handleModalClose: () => void;
  data?: CountryModel;
};

const CountryForm = ({ mode, handleModalClose, data }: Props) => {
  const initialValues = useMemo(
    () => ({
      name: "",
      code: "",
    }),
    []
  );

  const [formState, setFormState] = useState<FormProps>(initialValues);
  const dispatch = useAppDispatch();
  const displayToast = useAppSelector((state) => state.toast.displayToast);

  useEffect(() => {
    if (mode === "delete" && data) {
      setFormState(() => ({
        name: data.countryName!,
        code: data.countryCode!,
      }));
    }
  }, [mode, data]);

  const submitHandler = async (
    values: FormProps,
    actions: FormikHelpers<FormProps>
  ) => {
    try {
      let formData: CountryModel = {
        countryName: "",
        countryCode: "",
      };
      if (mode === "add") {
        formData.countryName = values.name;
        formData.countryCode = values.code;
      } else if (data) {
        formData = {
          ...data,
          countryName: values.name,
          countryCode: values.code,
        };
      }
      mode === "delete"
        ? await ProjectService.deleteCountry(formData)
        : await ProjectService.createCountry(formData, data?.countryCode);
      actions.setSubmitting(false);
      handleModalClose();
      dispatch(getAllCountries());
      displayToast(
        `Country ${mode === "add" ? "created" : "deleted"} successfully`,
        "success"
      );
    } catch (error) {
      actions.setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={formState}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      enableReinitialize={true}
    >
      {(formik) => (
        <>
          <Form>
            {mode === "delete" ? (
              <ConfirmDialog handleModalClose={handleModalClose} />
            ) : (
              <Stack gap={2}>
                <FormikControl
                  control="input"
                  type="text"
                  name="name"
                  size="small"
                  label="Country Name"
                  placeholder="Enter Name"
                />
                <FormikControl
                  control="input"
                  type="text"
                  name="code"
                  size="small"
                  label="Country Code"
                  placeholder="Enter Code"
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                >
                  Submit
                </Button>
              </Stack>
            )}
          </Form>
          {formik.isSubmitting && <Loader />}
        </>
      )}
    </Formik>
  );
};

export default CountryForm;
